import React, { useEffect } from "react";
import SectionToggle from "../../../../components/sectionToggle/sectionToggle";
import { useDispatch, useSelector } from "react-redux";
import { actionCreators } from "./redux/RiskMembersSagas";
import LoadingAnimation from "../../../../components/loadingAnimation/loadingAnimation";
import DatePicker from "./smartComponents/DatePicker";
import Scorecards from "./smartComponents/Scorecards";
import "./RiskMembersModule.css.scss";
import LearnMore from "../../../../components/learnMore/learnMore";
import ToggleableButton from "../../../../components/toggleableButton/toggleableButton";
import RiskMembersTable from "./smartComponents/RiskMembersTable";
import { DataPoints } from "./smartComponents/DataPoints";

const RiskMembersModule = ({ atRiskLearnData, ...rest }) => {
  const dispatch = useDispatch();
  const {
    isLoading,
    updatesLoading,
    initialRequest,
    scorecards,
    includeFutureVisits,
    table: { data = [] }
  } = useSelector((state) => state.riskMembers);

  useEffect(() => {
    if (!initialRequest) {
      dispatch(actionCreators.fetchCurrentUser({ ...rest }));
    }
  }, []);

  const content = () => {
    return (
      <div>
        <div id="override-edit-selection-bar" className="edit-selections-bar">
          <div className="top-filter">
            {data.length ? (
              <div className="toggle-button-wrapper">
                <p>Include Members with Future Bookings</p>
                <ToggleableButton
                  isActive={includeFutureVisits}
                  onClick={(isActive) => dispatch(actionCreators.handleToggle(isActive))}
                  title="risk-members-future-bookings"
                  isRadio
                />
              </div>
            ) : (
              <div></div>
            )}
            <div className="top-filter__right">
              <DatePicker />
            </div>
          </div>
        </div>
        {updatesLoading ? <LoadingAnimation uiLoader /> : data.length ? <DataPoints /> : null}
        <div data-testid="charts-container" className="chart-root">
          {updatesLoading ? (
            <LoadingAnimation text="Updating data..." />
          ) : (
            <>
              {scorecards.length ? <Scorecards scorecards={scorecards} /> : null}
              <RiskMembersTable />
            </>
          )}
        </div>
      </div>
    );
  };
  return (
    <div id="RiskMembers-root">
      <SectionToggle
        autoHeight
        learnMore={
          <LearnMore title="At Risk Members" content={learnMoreContent(atRiskLearnData)} />
        }
        title="At Risk Members"
        isLoading={isLoading}
        content={content()}
      />
    </div>
  );
};
const learnMoreContent = (atRiskLearnData) => {
  const {
    risk_members_days_from_last_visit,
    risk_members_days_prev_last_visit,
    example_date,
    start_date_with_visit,
    end_date_with_visit,
    start_date_without_visit,
    end_date_without_visit
  } = atRiskLearnData;

  return (
    <>
      <p>
        These <i>active</i> members have been identified as At Risk based on the recency of visits
        and the sudden stop in visits. Contact them now when there is still time to save them from
        terminating their membership. To enter the list, a member did not have a visit in the last{" "}
        {risk_members_days_from_last_visit} days but had a visit in the{" "}
        {risk_members_days_prev_last_visit} days previous.
      </p>
      <p>
        For example, if today is {example_date}st, the list will show members who had at least one
        visit between {start_date_with_visit} through {end_date_with_visit} but didn’t have a visit
        from {start_date_without_visit} to {end_date_without_visit}.
      </p>
      <p>
        The two At Risk members called out above the table are the two highest spending clients out
        of all members that match this criteria.
      </p>
      <p>Frozen members will NOT be included in the At Risk Members count.</p>
      <p>
        The toggle allows for the user to include or exclude at risk members with a future booking.
        As a default, we will include any member that has not had a visit even if they have a
        scheduled booking. When turning off the “Include Members with Future Bookings” toggle, a
        member who was put in At Risk based on the lack of visits, would be removed from the list if
        they have a future booking.
      </p>
      <p>
        Members will stay on this list until one of the following happens;
        <br />
      </p>
      <ol>
        <li>The member completes a visit.</li>
        <li>Their membership agreement is frozen.</li>
        <li>Their membership agreement is cancelled.</li>
        <li>
          The ‘Future Bookings’ toggle is turned on and the member has a future booking. In this
          case they are still considered an At Risk Member but removed from the view based on their
          future booking
        </li>
      </ol>
      <p>
        The members who have been contacted since their last visit date will fall under the
        Contacted metric while those that have not will fall under the Not Contacted. Only CR
        contact logs will be counted. Data will not include staff name or method type fields not
        filled out.
      </p>
    </>
  );
};

export default RiskMembersModule;
